import {
  Cart,
  CartTotalBody,
  CartTotalResponse,
  VOUCHER_REWARD_TYPE,
} from '@diamond/shared/types';
import {
  AppliedVoucher as StoreAppliedVoucher,
  areCartProductsEqual,
  CART_TOTAL_QUERY_KEY,
  normalizeCartTotalPayload,
  selectCartProductsForTotals,
  useCartStore,
  useShipToStore,
  useVoucherStore,
} from '@diamond/sol-buyer-context';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useDebounce } from 'usehooks-ts';
import shallow from 'zustand/shallow';

import {
  calculateDiscountPercentage,
  resolveDisplayPrice,
  resolvePromoDeductionAmount,
  resolvePromoIsDiscount,
} from '../price-calculation';
import { ProcessedPromo, processPromos } from '../promos/promos';

/**
 * Custom equality function for voucher codes
 * Prevents unnecessary re-renders when voucher array reference changes but content is same
 */
function areVoucherCodesEqual(
  vouchers1: StoreAppliedVoucher[],
  vouchers2: StoreAppliedVoucher[]
): boolean {
  if (vouchers1.length !== vouchers2.length) return false;
  return vouchers1.every((v, i) => v.code === vouchers2[i]?.code);
}

type AppliedVoucher = NonNullable<
  CartTotalResponse['vouchers']
>['applied_vouchers'][number];
type VoucherLookupKey = 'product_discounts' | 'free_goods';
type AppliedVoucherEntry<K extends VoucherLookupKey> = NonNullable<
  NonNullable<AppliedVoucher[K]>[number]
>;
type VoucherProductDiscount = AppliedVoucherEntry<'product_discounts'>;
type VoucherFreeGood = AppliedVoucherEntry<'free_goods'>;
type VoucherProductDiscountWithRewardType = VoucherProductDiscount & {
  reward_type: AppliedVoucher['reward_type'];
};

const findInAppliedVouchers = <K extends VoucherLookupKey>(
  cartTotal: CartTotalResponse | undefined,
  itemCode: number,
  discountType: K
): AppliedVoucherEntry<K> | null => {
  const vouchers = cartTotal?.vouchers?.applied_vouchers;

  if (!vouchers) {
    return null;
  }

  for (const voucher of vouchers) {
    const discountCollection = voucher[discountType];

    if (!discountCollection) {
      continue;
    }

    for (const discount of discountCollection) {
      if (discount.product_item_code === itemCode) {
        return discount as AppliedVoucherEntry<K>;
      }
    }
  }

  return null;
};

export interface PromoDisplayData {
  displayPrice: number;
  promoDeductionAmount: number;
  promoIsDiscount: boolean;
  isBundled: boolean;
  hasActiveDiscount: boolean;
  processedPromo: ProcessedPromo;
  isLoading: boolean;
  voucherDiscount: VoucherProductDiscountWithRewardType | null;
  hasVoucherDiscount: boolean;
  voucherFreeGoods: VoucherFreeGood | null;
  hasVoucherFreeGoods: boolean;
  /** Subtotal after discount - only populated for DISCOUNT_AMOUNT vouchers */
  subtotalAfterDiscount: number | null;
}

export const useCartItemPromoPrice = (
  item: Cart,
  isSelected: boolean
): PromoDisplayData => {
  const warehouse = useShipToStore((state) => state.selectedShipToId);
  const selectedProducts = useCartStore(
    selectCartProductsForTotals,
    areCartProductsEqual
  );
  const promoRecord = useCartStore(
    (state) => state.promoRecord,
    (a, b) => JSON.stringify(a) === JSON.stringify(b)
  );

  // Get free goods promos for this product
  const freeGoodsPromos = useCartStore(
    useCallback(
      (state) => state.freeGoodsPromoRecord[item.product.id] || [],
      [item.product.id]
    ),
    shallow
  );

  // Subscribe to store quantity for this item
  const storeQuantity = useCartStore(
    useCallback(
      (state) => state.cartRecord[item.id]?.quantity ?? item.quantity,
      [item.id, item.quantity]
    )
  );

  const queryClient = useQueryClient();

  // Use custom equality to prevent re-renders when voucher content hasn't changed
  const appliedVouchers = useVoucherStore(
    (state) => state.appliedVouchers,
    areVoucherCodesEqual
  );

  // Memoize voucher codes extraction to maintain stable reference
  const voucherCodes = useMemo(
    () => appliedVouchers.map((v) => v.code),
    [appliedVouchers]
  );

  const payload = useMemo(
    () =>
      ({
        warehouse_id: warehouse,
        is_return_promos: true,
        products: selectedProducts,
        voucher_codes: voucherCodes.length > 0 ? voucherCodes : undefined,
      } as CartTotalBody),
    [warehouse, selectedProducts, voucherCodes]
  );

  const debouncedPayload = useDebounce(payload, 500);
  const normalizedPayload = useMemo(
    () => normalizeCartTotalPayload(debouncedPayload),
    [debouncedPayload]
  );
  const queryKey = [CART_TOTAL_QUERY_KEY, normalizedPayload];
  const cartTotalData = queryClient.getQueryData<CartTotalResponse>(queryKey);

  // useIsFetching subscribes to query state changes, ensuring re-render when fetch completes
  // This fixes the endless loading issue where getQueryState doesn't trigger re-renders
  const fetchingCount = useIsFetching({ queryKey: [CART_TOTAL_QUERY_KEY] });
  const isFetching = fetchingCount > 0;

  // Show loading when selected and cart total is being fetched
  const isLoading = isSelected && isFetching;

  const voucherDiscount = useMemo(() => {
    if (!isSelected) {
      return null;
    }

    const vouchers = cartTotalData?.vouchers?.applied_vouchers;

    if (!vouchers) {
      return null;
    }

    for (const voucher of vouchers) {
      const discounts = voucher.product_discounts;

      if (!discounts) {
        continue;
      }

      for (const discount of discounts) {
        if (discount.product_item_code === item.product.item_code) {
          return {
            ...discount,
            reward_type: voucher.reward_type,
          } as VoucherProductDiscountWithRewardType;
        }
      }
    }

    return null;
  }, [cartTotalData, item.product.item_code, isSelected]);

  const hasVoucherDiscount = voucherDiscount !== null;

  const voucherFreeGoods = useMemo(() => {
    if (!isSelected) {
      return null;
    }

    return findInAppliedVouchers(
      cartTotalData,
      item.product.item_code,
      'free_goods'
    );
  }, [cartTotalData, item.product.item_code, isSelected]);

  const hasVoucherFreeGoods = voucherFreeGoods !== null;

  const findActivePromo = useCallback(
    (
      divisionPromos: CartTotalResponse['promos'][number],
      productId: string
    ) => {
      const { single_free_good_promos, bundled_free_good_promos } =
        divisionPromos;

      return [
        ...(single_free_good_promos?.filter(
          (promo) =>
            promo.ref_product_id === productId || promo.product_id === productId
        ) || []),
        ...(bundled_free_good_promos?.filter(
          (promo) => promo.product_id === productId
        ) || []),
      ];
    },
    []
  );

  const processedPromos = useMemo(() => {
    const divisionPromos = cartTotalData?.promos?.find(
      (p) => p.division === item.product.division
    );

    const activePromos = divisionPromos
      ? findActivePromo(divisionPromos, item.product.id)
      : [];

    const updatedProduct = activePromos?.length
      ? {
          ...item.product,
          free_good_promos:
            activePromos as typeof item.product.free_good_promos,
        }
      : item.product;

    const processed = processPromos(updatedProduct);

    if (hasVoucherDiscount && isSelected) {
      return {
        ...processed,
        hasDiscount: true,
      };
    }

    if (hasVoucherFreeGoods && isSelected && voucherFreeGoods) {
      return {
        ...processed,
        hasFreeGoods: true,
        qtyFreeGoods: voucherFreeGoods.quantity / 1000,
        minOrderQty: 0,
        isVoucherFreeGoods: true,
      };
    }

    // Override with calculated quantities from API when selected
    if (isSelected && freeGoodsPromos.length > 0) {
      const firstPromo = freeGoodsPromos[0];

      // Handle both single and bundled free goods promos
      const isSinglePromo = 'minimum_order_quantity' in firstPromo;
      const minimumOrderQuantity = isSinglePromo
        ? firstPromo.minimum_order_quantity
        : firstPromo.sum_minimum_order_quantity;
      const quantityFreeGoods = firstPromo.quantity_free_goods || 0;
      const isAppliesInMultiples = isSinglePromo
        ? firstPromo.is_applies_in_multiples
        : false;

      // Calculate multiplier to show actual purchase and free quantities
      const multiplier = isAppliesInMultiples
        ? Math.floor(storeQuantity / minimumOrderQuantity)
        : storeQuantity >= minimumOrderQuantity
        ? 1
        : 0;

      // Show effective purchase quantity and total free quantity when qualified
      const effectivePurchaseQty =
        multiplier > 0
          ? multiplier * minimumOrderQuantity
          : minimumOrderQuantity;
      const effectiveFreeQty =
        multiplier > 0 ? multiplier * quantityFreeGoods : quantityFreeGoods;

      return {
        ...processed,
        qtyFreeGoods: effectiveFreeQty / 1000,
        minOrderQty: effectivePurchaseQty / 1000,
        hasFreeGoods: true,
      };
    }

    return processed;
  }, [
    cartTotalData,
    item,
    findActivePromo,
    isSelected,
    freeGoodsPromos,
    storeQuantity,
    hasVoucherDiscount,
    hasVoucherFreeGoods,
    voucherFreeGoods,
  ]);

  const { hasDiscount, priceAfterDiscount, hasBundledDiscount } =
    processedPromos;

  const compiledPromo = promoRecord[item.product.id];
  const compiledPromoPrice = compiledPromo?.price_after_discount || 0;
  const hasCompiledDiscount = compiledPromoPrice > 0 && isSelected;
  const unitPrice = item.product.prices[0].price;

  const hasActiveDiscount = hasCompiledDiscount;
  const isBundled = hasBundledDiscount && !hasDiscount;
  const isQualifiedByQuantity =
    !hasActiveDiscount &&
    !isBundled &&
    item.product.minimum_order_quantity >=
      item.product.discount_promos[0]?.minimum_order_quantity;
  const isActive = hasActiveDiscount || isQualifiedByQuantity;

  const priceDisplayData = useMemo(() => {
    if (hasVoucherDiscount && voucherDiscount) {
      // Get product's base price per unit directly
      const productPrice = item.product.prices[0]?.price || 0;
      const isPercentDiscount =
        voucherDiscount.reward_type === VOUCHER_REWARD_TYPE.DISCOUNT_PERCENT;

      /**
       * Calculate Display Price Per Unit
       *
       * CASE 1: Voucher Discount Percentage
       * Formula: Unit Price * (1 - (Percent / 100))
       * Example: 100.000 * (1 - 0.20) = 80.000
       *
       * CASE 2: Voucher Discount Amount
       * Formula: Unit Price - Discount Amount
       * Example: 100.000 - 10.000 = 90.000
       */
      let discountedPricePerUnit: number;

      if (isPercentDiscount) {
        const discountFraction = voucherDiscount.discount_percent / 100;
        discountedPricePerUnit = productPrice * (1 - discountFraction);
      } else {
        discountedPricePerUnit = productPrice - voucherDiscount.discount_amount;
      }

      // Rounding: IDR has no decimals, so we round to the nearest integer
      const voucherDisplayPrice = Math.round(discountedPricePerUnit);

      /**
       * Calculate Badge Discount Percentage (Basis Points)
       *
       * CASE 1: Discount Percentage
       * Simply convert the percentage to basis points (x100)
       *
       * CASE 2: Discount Amount
       * Calculate effective percentage: (Amount / Unit Price) * 10000
       */
      let voucherDeductionAmountInBasisPoints: number;

      if (isPercentDiscount) {
        voucherDeductionAmountInBasisPoints =
          voucherDiscount.discount_percent * 100;
      } else {
        if (productPrice > 0) {
          voucherDeductionAmountInBasisPoints =
            (voucherDiscount.discount_amount / productPrice) * 10000;
        } else {
          voucherDeductionAmountInBasisPoints = 0;
        }
      }

      const safeVoucherDeductionAmount = Number.isFinite(
        voucherDeductionAmountInBasisPoints
      )
        ? voucherDeductionAmountInBasisPoints
        : 0;
      const voucherDiscountPercentage = calculateDiscountPercentage(
        safeVoucherDeductionAmount
      );

      return {
        displayPrice: voucherDisplayPrice,
        discountPercentage: voucherDiscountPercentage,
        promoDeductionAmount: safeVoucherDeductionAmount,
        promoIsDiscount: true,
        subtotalAfterDiscount: null,
      };
    }

    const hasRequiredPromoData =
      (hasCompiledDiscount && compiledPromo) ||
      (hasBundledDiscount && item.product.bundled_discount_promos?.length) ||
      (hasDiscount && item.product.discount_promos?.length);

    const hasAnyPromo =
      (hasCompiledDiscount && compiledPromo) ||
      hasBundledDiscount ||
      hasDiscount;

    const displayPrice = resolveDisplayPrice({
      hasCompiledDiscount,
      compiledPromoPrice,
      hasDiscount,
      isActive,
      priceAfterDiscount,
      unitPrice,
    });

    if (!hasAnyPromo || !hasRequiredPromoData) {
      return {
        displayPrice,
        discountPercentage: 0,
        promoDeductionAmount: 0,
        promoIsDiscount: false,
        subtotalAfterDiscount: null,
      };
    }

    const promoDeductionAmount = resolvePromoDeductionAmount({
      hasCompiledDiscount,
      compiledPromo,
      hasBundledDiscount,
      product: item.product,
    });

    const discountPercentage =
      calculateDiscountPercentage(promoDeductionAmount);

    const promoIsDiscount = resolvePromoIsDiscount({
      hasCompiledDiscount,
      compiledPromo,
      hasBundledDiscount,
      product: item.product,
    });

    return {
      displayPrice,
      discountPercentage,
      promoDeductionAmount,
      promoIsDiscount,
      subtotalAfterDiscount: null,
    };
  }, [
    hasVoucherDiscount,
    voucherDiscount,
    storeQuantity,
    hasCompiledDiscount,
    compiledPromoPrice,
    hasDiscount,
    isActive,
    priceAfterDiscount,
    unitPrice,
    compiledPromo,
    hasBundledDiscount,
    item.product,
  ]);

  return {
    displayPrice: priceDisplayData.displayPrice,
    promoDeductionAmount: priceDisplayData.promoDeductionAmount,
    promoIsDiscount: priceDisplayData.promoIsDiscount,
    isBundled,
    hasActiveDiscount: hasVoucherDiscount || hasCompiledDiscount,
    processedPromo: processedPromos,
    isLoading,
    voucherDiscount,
    hasVoucherDiscount,
    voucherFreeGoods,
    hasVoucherFreeGoods,
    subtotalAfterDiscount: priceDisplayData.subtotalAfterDiscount ?? null,
  };
};
